import React, { useState, useEffect, useCallback } from "react";
import { MISSIONS, type MissionId, type MissionDef } from "@/lib/simulation/missions";
import { useSimProgress } from "@/lib/simulation/progress";
import { ambientSound } from "@/lib/simulation/sound";
import UniverseHero from "./UniverseHero";
import { CheckCircle2, RotateCcw, Volume2, VolumeX, Compass, ShieldAlert, Sparkles, BookOpen } from "lucide-react";
export interface ControlCenterProps {
  currentMissionId: MissionId;
  onSelectMission: (id: MissionId) => void;
  onReplayIntro: () => void;
}
export default function ControlCenter({
  currentMissionId,
  onSelectMission,
  onReplayIntro
}: ControlCenterProps) {
  const {
    completed,
    clear
  } = useSimProgress();
  const [soundActive, setSoundActive] = useState(false);
  const [soundSupported, setSoundSupported] = useState(false);
  useEffect(() => {
    setSoundSupported(ambientSound.isSupported());
  }, []);
  const handleToggleSound = useCallback(() => {
    const next = ambientSound.toggle();
    setSoundActive(next);
  }, []);
  const handleClearProgress = useCallback(() => {
    if (typeof window !== "undefined") {
      const confirmed = window.confirm("Reset your simulation investigation progress in this browser?");
      if (confirmed) {
        clear();
      }
    }
  }, [clear]);
  const completedCount = completed.length;
  const totalMissions = MISSIONS.length;
  const progressPercent = Math.round(completedCount / totalMissions * 100);

  // Suggested next mission: first uncompleted mission in numerical order
  const suggestedNextDef = MISSIONS.find(m => !completed.includes(m.id)) || MISSIONS[0];
  return <section aria-label="Investigation Control Center" className="relative w-full max-w-6xl mx-auto px-4 py-8 md:py-12">
      {/* Top Header Bar: Observatory Title & Laboratory Subtitle */}
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 pb-6 border-b border-border/60">
        <div>
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-primary/30 bg-primary/10 font-mono text-xs font-semibold text-primary uppercase tracking-widest mb-3">
            <Compass className="w-3.5 h-3.5" />
            <span>INVESTIGATION CONTROL CENTER</span>
          </div>
          <h2 className="font-serif text-2xl sm:text-3xl md:text-4xl font-bold tracking-tight text-foreground">
            THE SIMULATION HYPOTHESIS
          </h2>
          <p className="font-sans italic text-muted-foreground text-sm sm:text-base mt-1.5 max-w-2xl">
            An investigation into reality, computation, consciousness, and the limits of what we can know.
          </p>
        </div>

        {/* Top utilities: Replay Intro, Sound Toggle */}
        <div className="flex items-center gap-3 self-start md:self-auto">
          {soundSupported && <button type="button" onClick={handleToggleSound} aria-pressed={soundActive} aria-label={soundActive ? "Mute ambient cosmic audio" : "Unmute ambient cosmic audio"} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-border bg-card/60 backdrop-blur-md text-foreground hover:border-primary hover:text-primary transition-colors text-xs min-h-[44px]">
              {soundActive ? <Volume2 className="w-4 h-4 text-primary" /> : <VolumeX className="w-4 h-4 text-muted-foreground" />}
              <span>{soundActive ? "Sound on" : "Sound off"}</span>
            </button>}

          <button type="button" onClick={onReplayIntro} aria-label="Replay introductory animation" className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-border bg-card/60 backdrop-blur-md text-muted-foreground hover:border-primary hover:text-foreground transition-colors text-xs min-h-[44px]">
            <RotateCcw className="w-3.5 h-3.5" />
            <span>Replay intro</span>
          </button>
        </div>
      </div>

      {/* Central Visual Stage & Live Progress Telemetry Panels */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 my-8 items-stretch">
        {/* Central Universe Hero Canvas */}
        <div className="lg:col-span-8 flex flex-col justify-center">
          <UniverseHero />
        </div>

        {/* Live Data Panels (REAL live values only; NO fake telemetry) */}
        <div className="lg:col-span-4 flex flex-col justify-between gap-4">
          {/* Mission Progress Panel */}
          <div className="p-5 rounded-xl border border-border bg-card/50 backdrop-blur-md flex flex-col justify-between">
            <div>
              <div className="flex items-center justify-between text-xs font-mono text-muted-foreground mb-2">
                <span className="uppercase tracking-wider">INVESTIGATION PROGRESS</span>
                <span className="text-primary font-bold">{progressPercent}%</span>
              </div>
              <div className="font-serif text-2xl font-bold text-foreground">
                {completedCount} <span className="text-sm font-sans font-normal text-muted-foreground">of {totalMissions} missions explored</span>
              </div>

              {/* Progress bar */}
              <div className="w-full h-2 rounded-full bg-muted/60 overflow-hidden mt-3 border border-border/40">
                <div className="h-full bg-primary transition-all duration-500 rounded-full shadow-[0_0_8px_var(--glow-primary)]" style={{
                width: `${progressPercent}%`
              }} />
              </div>
            </div>

            {/* Current Active Mission Status */}
            <div className="mt-5 pt-4 border-t border-border/40">
              <span className="text-[0.6875rem] font-mono uppercase tracking-widest text-muted-foreground">ACTIVE FOCUS</span>
              <div className="font-serif text-base font-semibold text-primary mt-0.5">
                {MISSIONS.find(m => m.id === currentMissionId)?.title}
              </div>
              <p className="font-sans text-xs text-muted-foreground mt-1 line-clamp-2">
                {MISSIONS.find(m => m.id === currentMissionId)?.tagline}
              </p>
            </div>
          </div>

          {/* Privacy & Browser Storage Note */}
          <div className="p-4 rounded-xl border border-border/60 bg-muted/30 text-xs font-sans text-muted-foreground flex flex-col justify-between gap-3">
            <div className="flex items-start gap-2.5">
              <ShieldAlert className="w-4 h-4 text-primary shrink-0 mt-0.5" />
              <div>
                <p className="text-foreground font-medium text-xs">Local &amp; Private Exploration</p>
                <p className="text-[0.75rem] text-muted-foreground mt-0.5 leading-relaxed">
                  Stored only in this browser. No account, no personal data, no server transmission.
                </p>
              </div>
            </div>

            {completedCount > 0 && <div className="pt-2 border-t border-border/30 flex justify-end">
                <button type="button" onClick={handleClearProgress} className="text-xs text-muted-foreground hover:text-destructive transition-colors underline underline-offset-4 min-h-[44px] flex items-center px-1">
                  Clear my progress
                </button>
              </div>}
          </div>
        </div>
      </div>

      {/* Visual Journey Map: Connected Stepper of 10 Missions */}
      <div className="mt-10 mb-4">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-4">
          <div className="flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-primary" />
            <h3 className="font-serif text-lg font-bold tracking-wide text-foreground">
              INVESTIGATION PATHWAY
            </h3>
          </div>
          <span className="font-mono text-xs text-muted-foreground">
            All 10 missions open &bull; Jump freely or follow the sequence
          </span>
        </div>

        {/* Desktop / Tablet: scroll-snap stepper without overflowing page */}
        {/* Mobile: 2-column or stacked grid */}
        <div className="w-full">
          <nav aria-label="Missions journey navigation" className="grid grid-cols-1 min-[480px]:grid-cols-2 md:grid-cols-5 gap-3">
            {MISSIONS.map((mission: MissionDef) => {
            const isCurrent = mission.id === currentMissionId;
            const isDone = completed.includes(mission.id);
            const isSuggested = !isDone && mission.id === suggestedNextDef.id;
            return <button key={mission.id} type="button" onClick={() => onSelectMission(mission.id)} aria-current={isCurrent ? "true" : undefined} className={`group relative text-left p-3.5 rounded-xl border transition-all min-h-[88px] flex flex-col justify-between select-none ${isCurrent ? "border-primary bg-primary/10 shadow-[0_0_16px_var(--glow-primary)]" : isDone ? "border-emerald-500/40 bg-card/60 hover:border-emerald-500/70" : isSuggested ? "border-blue-500/50 bg-card/40 hover:border-primary" : "border-border bg-card/40 hover:border-primary/60 hover:bg-card/70"}`}>
                  {/* Top line: Number badge + status indicator */}
                  <div className="flex items-center justify-between gap-2">
                    <span className={`font-mono text-[0.6875rem] font-bold px-1.5 py-0.5 rounded ${isCurrent ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground group-hover:text-foreground"}`}>
                      {String(mission.number).padStart(2, "0")}
                    </span>

                    {isDone ? <span className="inline-flex items-center text-emerald-400 text-xs font-mono gap-1" title="Completed">
                        <CheckCircle2 className="w-3.5 h-3.5" />
                        <span className="sr-only">Explored</span>
                      </span> : isSuggested ? <span className="font-mono text-[0.625rem] uppercase tracking-wider text-blue-400 bg-blue-500/10 px-1.5 py-0.5 rounded border border-blue-500/30">
                        Next
                      </span> : null}
                  </div>

                  {/* Title & Tagline */}
                  <div className="mt-2">
                    <div className={`font-serif text-sm font-semibold leading-tight line-clamp-1 ${isCurrent ? "text-primary" : "text-foreground group-hover:text-primary transition-colors"}`}>
                      {mission.title}
                    </div>
                    <div className="font-sans text-[0.75rem] text-muted-foreground line-clamp-2 mt-1 leading-snug">
                      {mission.tagline}
                    </div>
                  </div>

                  {/* Active highlight marker */}
                  {isCurrent && <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-6 h-1 rounded-full bg-primary" />}
                </button>;
          })}
          </nav>
        </div>
      </div>
    </section>;
}